import { useCallback } from "react";
import { ICategoryFormModel } from "../components/CategoryFormDialog";
import { INomenclatureCategory } from "../models/categorizedNomenclatureModels";

interface UseCategoryHandlersParams<TCategory extends INomenclatureCategory> {
  setCategoryDialogOpen: (open: boolean) => void;
  setEditingCategoryId: (id: number | null) => void;
  createCategory: (data: Partial<TCategory>) => void;
  updateCategory: (id: number, data: Partial<TCategory>) => void;
}

export function useCategoryHandlers<TCategory extends INomenclatureCategory>({
  setCategoryDialogOpen,
  setEditingCategoryId,
  createCategory,
  updateCategory,
}: UseCategoryHandlersParams<TCategory>) {
  const handleAddCategoryClick = useCallback(() => {
    setEditingCategoryId(null);
    setCategoryDialogOpen(true);
  }, [setCategoryDialogOpen, setEditingCategoryId]);

  const onEditCategoryClick = useCallback(
    (categoryId: number) => {
      setEditingCategoryId(categoryId);
      setCategoryDialogOpen(true);
    },
    [setCategoryDialogOpen, setEditingCategoryId]
  );

  const onHideCategoryDialog = useCallback(() => {
    setCategoryDialogOpen(false);
    setEditingCategoryId(null);
  }, [setCategoryDialogOpen, setEditingCategoryId]);

  const onSubmitCategory = useCallback(
    (data: ICategoryFormModel) => {
      if (data.id) {
        updateCategory(data.id, data as Partial<TCategory>);
      } else {
        createCategory(data as Partial<TCategory>);
      }
    },
    [createCategory, updateCategory]
  );

  return {
    handleAddCategoryClick,
    onEditCategoryClick,
    onHideCategoryDialog,
    onSubmitCategory,
  };
}
